import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { Heart, Diamond, Coins, CreditCard, X, Gift, Check } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";

const DonateModal = ({ onClose }) => {
  const { token, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("diamonds"); // diamonds, fchips, cash
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fchipsPurchaseAmount, setFchipsPurchaseAmount] = useState(1);

  // User balances - fetch from API for accurate values
  const [diamondBalance, setDiamondBalance] = useState(0);
  const [fchipBalance, setFchipBalance] = useState(0);

  // Fetch balances on mount
  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const [diamondRes, fchipRes] = await Promise.all([
          axios.get(`${API}/diamonds/balance`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/fchips/balance`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setDiamondBalance(diamondRes.data.balance || 0);
        setFchipBalance(fchipRes.data.balance || 0);
      } catch (error) {
        console.error("Failed to fetch balances:", error);
      }
    };
    if (token) {
      fetchBalances();
    }
  }, [token]);

  const handleDonateCurrency = async (currency) => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const amountNum = parseFloat(amount);
    const balance = currency === "diamonds" ? diamondBalance : fchipBalance;

    if (amountNum > balance) {
      toast.error(`Insufficient ${currency} balance`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/donate`, {
        currency,
        amount: amountNum,
        message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Thank you for donating ${amountNum} ${currency}! ❤️`);
      setAmount("");
      setMessage("");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Donation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchaseFchips = async () => {
    if (fchipsPurchaseAmount < 1) {
      toast.error("Minimum purchase is $1");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/fchips/purchase`, {
        amount_dollars: fchipsPurchaseAmount,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { checkout_url, session_id } = response.data;
      if (!checkout_url) {
        toast.error("Payment could not be started");
        return;
      }
      try {
        sessionStorage.setItem("pending_checkout_session", session_id);
        sessionStorage.setItem("pending_checkout_kind", "fchips_purchase");
      } catch (_e) { /* noop */ }
      toast.info("Redirecting to Stripe Checkout…");
      window.location.href = checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed");
      setSubmitting(false);
    }
  };

  return (
    <DraggableResizableWindow
      title={<><Heart className="w-4 h-4 text-red-400" /> Donate to MEGA!</>}
      onClose={onClose}
      initialSize={{ width: 450, height: 520 }}
      minSize={{ width: 380, height: 400 }}
      zIndex={6000}
      titleBarColor="bg-gradient-to-r from-pink-600 to-red-600"
    >
      <div className="p-4 bg-gradient-to-b from-pink-50 to-red-50 h-full overflow-auto retro-scrollbar" data-testid="donate-modal">
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold text-red-700 flex items-center justify-center gap-2">
            <Gift className="w-6 h-6" />
            Support MEGA!
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Your donations help keep the site running and fund prizes!
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-gray-200 p-1 rounded">
          <button
            className={`flex-1 py-2 px-3 text-xs font-bold rounded flex items-center justify-center gap-1 ${
              activeTab === "diamonds" ? "bg-cyan-500 text-white" : "hover:bg-gray-300"
            }`}
            onClick={() => setActiveTab("diamonds")}
          >
            <Diamond className="w-4 h-4" /> Diamonds
          </button>
          <button
            className={`flex-1 py-2 px-3 text-xs font-bold rounded flex items-center justify-center gap-1 ${
              activeTab === "fchips" ? "bg-green-500 text-white" : "hover:bg-gray-300"
            }`}
            onClick={() => setActiveTab("fchips")}
            data-testid="donate-fchips-tab"
          >
            <Coins className="w-4 h-4" /> F-Chips
          </button>
          <button
            className={`flex-1 py-2 px-3 text-xs font-bold rounded flex items-center justify-center gap-1 ${
              activeTab === "cash" ? "bg-purple-500 text-white" : "hover:bg-gray-300"
            }`}
            onClick={() => setActiveTab("cash")}
          >
            <CreditCard className="w-4 h-4" /> Credit Card
          </button>
        </div>

        {/* Diamond Donation */}
        {activeTab === "diamonds" && (
          <div className="space-y-4">
            <div className="retro-inset p-3 bg-cyan-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-cyan-700">Your Diamond Balance:</span>
                <span className="text-lg font-bold text-cyan-800">{diamondBalance.toLocaleString()} 💎</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Amount to Donate:</label>
              <input
                type="number"
                min="1"
                max={diamondBalance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="retro-inset w-full px-3 py-2 text-sm"
                placeholder="Enter diamond amount"
              />
            </div>

            {/* Quick amounts */}
            <div className="flex gap-2">
              {[10, 50, 100, 500].map(amt => (
                <button
                  key={amt}
                  className="retro-button flex-1 py-1 text-xs"
                  onClick={() => setAmount(amt.toString())}
                  disabled={amt > diamondBalance}
                >
                  {amt} 💎
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Message (optional):</label>
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="retro-inset w-full px-3 py-2 text-sm"
                placeholder="Leave a message..."
                maxLength={100}
              />
            </div>

            <button
              className="retro-button w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold text-sm flex items-center justify-center gap-2"
              onClick={() => handleDonateCurrency("diamonds")}
              disabled={submitting || !amount}
            >
              <Heart className="w-4 h-4" />
              {submitting ? "Processing..." : `Donate ${amount || 0} Diamonds`}
            </button>
          </div>
        )}

        {/* F-Chips Donation */}
        {activeTab === "fchips" && (
          <div className="space-y-4">
            <div className="retro-inset p-3 bg-green-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-green-700">Your F-Chips Balance:</span>
                <span className="text-lg font-bold text-green-800">{fchipBalance.toLocaleString()} 🎰</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Amount to Donate:</label>
              <input
                type="number"
                min="1"
                max={fchipBalance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="retro-inset w-full px-3 py-2 text-sm"
                placeholder="Enter F-Chips amount"
              />
            </div>

            {/* Quick amounts */}
            <div className="flex gap-2">
              {[100, 500, 1000, 5000].map(amt => (
                <button
                  key={amt}
                  className="retro-button flex-1 py-1 text-xs"
                  onClick={() => setAmount(amt.toString())}
                  disabled={amt > fchipBalance}
                >
                  {amt} 🎰
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Message (optional):</label>
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="retro-inset w-full px-3 py-2 text-sm"
                placeholder="Leave a message..."
                maxLength={100}
              />
            </div>

            <button
              className="retro-button w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2"
              onClick={() => handleDonateCurrency("fchips")}
              disabled={submitting || !amount}
            >
              <Heart className="w-4 h-4" />
              {submitting ? "Processing..." : `Donate ${amount || 0} F-Chips`}
            </button>

            {/* Buy more F-Chips */}
            <div className="border-t border-gray-300 pt-4 mt-4">
              <h3 className="text-sm font-bold mb-2">Need more F-Chips?</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs">$</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={fchipsPurchaseAmount}
                  onChange={(e) => setFchipsPurchaseAmount(parseInt(e.target.value) || 1)}
                  className="retro-inset w-20 px-2 py-1 text-sm"
                  data-testid="fchips-purchase-amount-input"
                />
                <span className="text-xs">= {(fchipsPurchaseAmount * 1000).toLocaleString()} F-Chips</span>
                <button
                  className="retro-button px-3 py-1 text-xs bg-purple-100"
                  onClick={handlePurchaseFchips}
                  disabled={submitting}
                  data-testid="buy-fchips-btn"
                >
                  Buy via Stripe
                </button>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">$1 = 1,000 F-Chips</p>
            </div>
          </div>
        )}

        {/* Credit Card Donation */}
        {activeTab === "cash" && (
          <div className="space-y-4">
            <div className="retro-inset p-4 bg-purple-50 text-center">
              <CreditCard className="w-12 h-12 text-purple-500 mx-auto mb-2" />
              <h3 className="font-bold text-purple-700">Direct Cash Donation</h3>
              <p className="text-sm text-gray-600 mt-1">
                Support MEGA! with a credit card donation via Stripe
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[5, 10, 25, 50].map(amt => (
                <button
                  key={amt}
                  className="retro-button py-3 text-sm font-bold bg-gradient-to-r from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200"
                  onClick={() => toast.info("Stripe checkout coming soon!")}
                >
                  ${amt}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Custom Amount:</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  placeholder="Enter amount"
                  className="retro-inset flex-1 px-3 py-2 text-sm"
                />
                <button className="retro-button px-4 py-2 bg-purple-500 text-white font-bold">
                  Donate
                </button>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 mt-4">
              <p>Secure payments powered by Stripe</p>
              <p className="mt-1">100% of donations go to site maintenance and prizes</p>
            </div>
          </div>
        )}

        {/* Thank you message */}
        <div className="mt-4 p-3 bg-gradient-to-r from-pink-100 to-red-100 rounded text-center">
          <p className="text-sm font-bold text-red-700">
            ❤️ Thank you for supporting MEGA! ❤️
          </p>
          <p className="text-xs text-gray-600 mt-1">
            All donations help keep this community alive!
          </p>
        </div>
      </div>
    </DraggableResizableWindow>
  );
};

export default DonateModal;
